
import React from "react";
import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { useLanguage } from "@/components/LanguageContext";
import { useLanguagePath } from "@/utils/language";
import { Navigation } from "@/components/Navigation";
import { Benefits } from "@/components/landing/Benefits";
import { Footer } from "@/components/landing/Footer";
import { ChevronRight, ShieldCheck, Zap, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";

const BenefitsPage: React.FC = () => {
  const { language } = useLanguage();
  const { getPath } = useLanguagePath();

  const getTitle = () => {
    switch(language) {
      case 'pt': return "TrainerFOLIO - Benefícios";
      case 'es': return "TrainerFOLIO - Beneficios";
      case 'fr': return "TrainerFOLIO - Avantages";
      default: return "TrainerFOLIO - Benefits";
    }
  };

  const getDescription = () => {
    switch(language) {
      case 'pt': return "Descubra os benefícios da plataforma TrainerFOLIO para seu trabalho como Personal Trainer.";
      case 'es': return "Descubra los beneficios de la plataforma TrainerFOLIO para su trabajo como Entrenador Personal.";
      case 'fr': return "Découvrez les avantages de la plateforme TrainerFOLIO pour votre travail de Personal Trainer.";
      default: return "Discover the benefits of the TrainerFOLIO platform for your work as a Personal Trainer.";
    }
  };

  const getHomeText = () => {
    switch(language) {
      case 'pt': return 'Início';
      case 'es': return 'Inicio';
      case 'fr': return 'Accueil';
      default: return 'Home';
    }
  };

  const getBenefitsText = () => {
    switch(language) {
      case 'pt': return 'Benefícios';
      case 'es': return 'Beneficios';
      case 'fr': return 'Avantages';
      default: return 'Benefits';
    }
  };

  const getMoreBenefitsText = () => {
    switch(language) {
      case 'pt': return 'Mais Benefícios';
      case 'es': return 'Más Beneficios';
      case 'fr': return 'Plus d\'Avantages';
      default: return 'More Benefits';
    }
  };

  const getDescriptionText = () => {
    switch(language) {
      case 'pt': return 'Entenda como o TrainerFOLIO pode transformar sua experiência como Personal Trainer.';
      case 'es': return 'Entienda cómo TrainerFOLIO puede transformar su experiencia como Entrenador Personal.';
      case 'fr': return 'Comprenez comment TrainerFOLIO peut transformer votre expérience en tant que Personal Trainer.';
      default: return 'Understand how TrainerFOLIO can transform your experience as a Personal Trainer.';
    }
  };

  const getStartedText = () => {
    switch(language) {
      case 'pt': return 'Começar agora';
      case 'es': return 'Comenzar ahora';
      case 'fr': return 'Commencer maintenant';
      default: return 'Get started';
    }
  };

  // Benefit titles
  const getProductivityTitle = () => {
    switch(language) {
      case 'pt': return 'Aumente sua Produtividade';
      case 'es': return 'Aumente su Productividad';
      case 'fr': return 'Augmentez votre Productivité';
      default: return 'Increase Your Productivity';
    }
  };

  const getRetentionTitle = () => {
    switch(language) {
      case 'pt': return 'Melhore a Retenção de Alunos';
      case 'es': return 'Mejore la Retención de Estudiantes';
      case 'fr': return 'Améliorez la Fidélisation des Élèves';
      default: return 'Improve Student Retention';
    }
  };

  const getGrowthTitle = () => {
    switch(language) {
      case 'pt': return 'Expanda seu Negócio';
      case 'es': return 'Expanda su Negocio';
      case 'fr': return 'Développez votre Entreprise';
      default: return 'Grow Your Business';
    }
  };

  // Benefit descriptions
  const getProductivityDesc = () => {
    switch(language) {
      case 'pt': return 'Economize tempo com automação e ferramentas que agilizam seu trabalho.';
      case 'es': return 'Ahorre tiempo con automatización y herramientas que agilizan su trabajo.';
      case 'fr': return 'Économisez du temps avec l\'automatisation et des outils qui simplifient votre travail.';
      default: return 'Save time with automation and tools that streamline your work.';
    }
  };

  const getRetentionDesc = () => {
    switch(language) {
      case 'pt': return 'Melhore a experiência dos seus alunos e aumente as taxas de retenção.';
      case 'es': return 'Mejore la experiencia de sus estudiantes y aumente las tasas de retención.';
      case 'fr': return 'Améliorez l\'expérience de vos élèves et augmentez les taux de fidélisation.';
      default: return 'Improve your students\' experience and increase retention rates.';
    }
  };

  const getGrowthDesc = () => {
    switch(language) {
      case 'pt': return 'Construa uma presença online sólida e atraia novos clientes.';
      case 'es': return 'Construya una presencia online sólida y atraiga nuevos clientes.';
      case 'fr': return 'Construisez une présence en ligne solide et attirez de nouveaux clients.';
      default: return 'Build a solid online presence and attract new clients.';
    }
  };

  // Dados para o schema.org
  const schemaData = {
    headline: getTitle(),
    description: getDescription(),
    author: {
      "@type": "Organization",
      name: "TrainerFOLIO"
    },
    publisher: {
      "@type": "Organization",
      name: "TrainerFOLIO",
      logo: {
        "@type": "ImageObject",
        url: "https://trainerfolio.com/lovable-uploads/76235432-f790-4bd0-a843-a378bbe969d9.png"
      }
    },
    datePublished: "2024-01-15T12:00:00Z",
    dateModified: new Date().toISOString()
  };

  return (
    <div className="flex flex-col min-h-screen">
      <SEOHead 
        title={getTitle()}
        description={getDescription()}
        canonicalPath={getPath('/benefits')}
        schemaType="Article"
        schemaData={schemaData}
      />

      <header>
        <Navigation />
      </header>

      <main className="flex-grow bg-gradient-to-b from-slate-50 to-white dark:from-gray-900 dark:to-gray-950">
        {/* Breadcrumbs */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 mt-20">
          <nav className="flex items-center text-sm text-slate-500 dark:text-slate-400">
            <Link to={getPath('/')} className="hover:text-flame">
              {getHomeText()}
            </Link>
            <ChevronRight className="h-4 w-4 mx-2" />
            <span className="font-medium text-slate-700 dark:text-slate-300">
              {getBenefitsText()}
            </span>
          </nav>
        </div>

        {/* Page Header */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="text-4xl font-bold text-black dark:text-white">
            {getBenefitsText()}
          </h1>
          <p className="mt-4 text-xl text-slate-600 dark:text-slate-400 max-w-3xl">
            {getDescriptionText()}
          </p>

          <Button asChild className="mt-6">
            <Link to={getPath('/auth')}>
              {getStartedText()}
            </Link>
          </Button>
        </div>

        {/* Benefits Section */}
        <Benefits />

        {/* Additional Benefits Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="text-3xl font-bold text-black dark:text-white mb-8">
            {getMoreBenefitsText()}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md">
              <ShieldCheck className="h-12 w-12 text-flame mb-4" />
              <h3 className="text-xl font-semibold mb-2 dark:text-white">
                {getProductivityTitle()}
              </h3>
              <p className="text-slate-600 dark:text-slate-300">
                {getProductivityDesc()}
              </p>
            </div>
            
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md">
              <Zap className="h-12 w-12 text-flame mb-4" />
              <h3 className="text-xl font-semibold mb-2 dark:text-white">
                {getRetentionTitle()}
              </h3>
              <p className="text-slate-600 dark:text-slate-300">
                {getRetentionDesc()}
              </p>
            </div>
            
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md">
              <Clock className="h-12 w-12 text-flame mb-4" />
              <h3 className="text-xl font-semibold mb-2 dark:text-white">
                {getGrowthTitle()}
              </h3>
              <p className="text-slate-600 dark:text-slate-300">
                {getGrowthDesc()}
              </p>
            </div>
          </div>
        </div>
      </main>

      <footer>
        <Footer />
      </footer>
    </div>
  );
};

export default BenefitsPage;
